/**
 * Operating Points Configuration - SINGLE SOURCE OF TRUTH
 *
 * This file defines all operating point related constants used across:
 * - Worklist (findings display)
 * - PredictionsDisplay (case viewer findings)
 * - Report generation (filtering)
 * - CohortCaseViewer
 *
 * Operating points represent confidence thresholds, ordered from
 * highest confidence (most specific) to lowest (most sensitive):
 *   high_specificity > balanced > sensitivity_biased > high_sensitivity
 */

// Operating point levels ordered from highest to lowest confidence
export const OP_LEVELS = [
  "high_specificity",
  "balanced",
  "sensitivity_biased",
  "high_sensitivity",
] as const;
export type OpLevel = (typeof OP_LEVELS)[number];

// Reverse order (lowest to highest) - used by some backend transforms
export const OP_LEVELS_ASCENDING = [...OP_LEVELS].reverse() as readonly OpLevel[];

// Human-readable labels
export const OP_LABELS: Record<OpLevel, string> = {
  high_specificity: "High Confidence",
  balanced: "Medium Confidence",
  sensitivity_biased: "Lower Confidence",
  high_sensitivity: "Low Confidence",
};

// Short labels for compact displays
export const OP_SHORT_LABELS: Record<OpLevel, string> = {
  high_specificity: "HS",
  balanced: "BA",
  sensitivity_biased: "SB",
  high_sensitivity: "HSe",
};

// Full operating-point names (specificity/sensitivity framing). Distinct from
// OP_LABELS, which is the confidence framing ("High Confidence", …). Use when
// the abbreviations are ambiguous (HS reads as both High Specificity and High
// Sensitivity).
export const OP_FULL_NAMES: Record<OpLevel, string> = {
  high_specificity: "High Specificity",
  balanced: "Balanced",
  sensitivity_biased: "Sensitivity Biased",
  high_sensitivity: "High Sensitivity",
};

// Dot indicators for visual confidence display (4 dots) - legacy
export const OP_DOT_INDICATORS: Record<OpLevel, string> = {
  high_specificity: "●●●●",
  balanced: "●●●○",
  sensitivity_biased: "●●○○",
  high_sensitivity: "●○○○",
};

// Bar indicators for confidence display (filled vs empty)
export const OP_BAR_COUNT: Record<OpLevel, number> = {
  high_specificity: 4,
  balanced: 3,
  sensitivity_biased: 2,
  high_sensitivity: 1,
};

export type DeviceType = "drafter" | "ct-suite" | "cortex";

// HAND-MIRRORED with demo-backend/src/device_type.py DEVICE_TYPE_PATTERNS —
// keep both in lockstep (same keys, same patterns, same cortex-first order).
// No CI guard binds them; editing one without the other is what broke the
// lyra/soma worklist. Durable fix (backend-provided config or a cross-repo
// parity check) tracked in a2zrai/web-demo-backend#462.
const DEVICE_TYPE_PATTERNS: Record<DeviceType, string[]> = {
  // cortex first — matches "cortex" before "drafter"/"ct-suite" patterns.
  // a2z-lyra is cortex_v1p1 and a2z-soma is carried over from lyra (same
  // structured phrasing + per-finding recommended_threshold), so both use the
  // cortex device config. a2z-kidney-stone is a CADe device sharing soma's label
  // space with the same per-finding gating. The surfacer and Report Concordance
  // (a2z-report-concordance-v*, the device-engine extension) run on the Soma
  // base, so they map like soma — as the backend registry does. "surfacer" used
  // to sit under ct-suite here while the backend mapped it to cortex (and
  // "soma-surfacer" already resolved to cortex via "soma"). Without an entry,
  // detectDeviceType throws "Unknown device model" and the worklist query fails
  // into the "couldn't load this list" state.
  cortex: ["cortex", "lyra", "soma", "kidney-stone", "surfacer", "report-concordance"],
  drafter: ["drafter"],
  "ct-suite": [
    "ct-suite",
    "abdomen-pelvis",
    "abdo-triage",
    "chest-triage",
    "omni-triage",
    "unified-triage",
    "titan",
    "reporter",
  ],
};

// Detection threshold per device. ct-suite uses a single OpLevel (cascade
// applies — strict ⊂ looser). cortex AND drafter use each finding's own
// `recommended_threshold`, surfaced via the `detected_at_recommended` worklist
// field — not a single OpLevel.
//
// HAND-MIRRORED with demo-backend src/device_type.py DEVICE_CONFIGS
// `detection_strategy`: that value gates the report, this one gates the
// worklist. Editing one without the other makes a study's findings count
// differ between the two views. No CI guard binds them — durable fix tracked
// in web-demo-backend#462.
export type DetectedThreshold = OpLevel | "recommended";
export const DEVICE_DETECTED_THRESHOLD: Record<DeviceType, DetectedThreshold> = {
  // drafter: per-finding, like cortex. "high_sensitivity" was not a filter — the cascade is
  // cumulative, so the loosest rung is true for every finding the engine emitted, and the
  // worklist showed ~2x the claimed set (19,111 vs 9,578 on the v2.0.0 reference run).
  drafter: "recommended",
  "ct-suite": "high_specificity",
  cortex: "recommended",
};

export function detectDeviceType(model: string): DeviceType {
  if (!model) throw new Error("model is required for device type detection");
  const m = model.toLowerCase();
  for (const [type, patterns] of Object.entries(DEVICE_TYPE_PATTERNS) as Array<
    [DeviceType, string[]]
  >) {
    if (patterns.some((p) => m.includes(p))) return type;
  }
  throw new Error(`Unknown device model: ${model}`);
}

export function getDetectedThreshold(model: string): DetectedThreshold {
  return DEVICE_DETECTED_THRESHOLD[detectDeviceType(model)];
}

// Color scheme for confidence levels (for badges, indicators, etc.)
export const OP_COLORS: Record<
  OpLevel,
  { bg: string; border: string; text: string; solid: string }
> = {
  high_specificity: {
    bg: "bg-green-500/20",
    border: "border-green-500/40",
    text: "text-green-300",
    solid: "bg-green-500",
  },
  balanced: {
    bg: "bg-blue-500/20",
    border: "border-blue-500/40",
    text: "text-blue-300",
    solid: "bg-blue-500",
  },
  sensitivity_biased: {
    bg: "bg-yellow-500/20",
    border: "border-yellow-500/40",
    text: "text-yellow-300",
    solid: "bg-yellow-500",
  },
  high_sensitivity: {
    bg: "bg-orange-500/20",
    border: "border-orange-500/40",
    text: "text-orange-300",
    solid: "bg-orange-500",
  },
};

// Opacity values for visual prominence based on confidence
export const OP_OPACITY: Record<OpLevel, number> = {
  high_specificity: 1.0,
  balanced: 0.85,
  sensitivity_biased: 0.7,
  high_sensitivity: 0.55,
};
